import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PLATFORMS } from '../data/initial';

function fmtNum(n) {
  if (!n || n === 0) return '';
  return Number(n).toLocaleString('en-GB');
}

function prevMonth(month) {
  const [y, mo] = month.split('-').map(Number);
  return mo === 1 ? `${y-1}-12` : `${y}-${String(mo-1).padStart(2,'0')}`;
}

function DeltaBadge({ curr, prev }) {
  if (!prev || !curr) return <span style={{ color:'var(--text3)' }}>—</span>;
  const d = curr - prev;
  if (d === 0) return <span style={{ color:'var(--text3)' }}>—</span>;
  return (
    <span style={{ fontSize:12, fontWeight:600, color: d > 0 ? 'var(--green)' : 'var(--red)' }}>
      {d > 0 ? '+' : ''}{d.toLocaleString('en-GB')}
    </span>
  );
}

function EditableCell({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const startEdit = () => {
    setDraft(value ? String(value) : '');
    setEditing(true);
  };

  const commit = () => {
    const parsed = parseInt(draft) || 0;
    onSave(parsed);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        type="number"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        autoFocus
        style={{
          width: '100%', minWidth: 80,
          padding: '6px 8px', borderRadius: 7,
          border: '1.5px solid var(--accent)',
          background: 'var(--surface3)', color: 'var(--text)',
          fontSize: 14, fontFamily: 'DM Sans, sans-serif',
          textAlign: 'right',
        }}
      />
    );
  }

  return (
    <div
      onClick={startEdit}
      style={{
        cursor: 'text', minHeight: 32,
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        padding: '4px 6px', borderRadius: 6,
        color: value ? 'var(--text)' : 'var(--text3)',
        fontWeight: value ? 500 : 400,
        fontSize: 14,
        transition: 'background .15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface3)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      title="Click to edit"
    >
      {value ? fmtNum(value) : <span style={{ fontSize: 12, opacity: 0.4 }}>—</span>}
    </div>
  );
}

export default function FollowersView({ store }) {
  const { state, getFollowerCount, setFollowerCount } = store;
  const { currentMonth, brands, managers } = state;
  const prev = prevMonth(currentMonth);

  const [filterMgr, setFilterMgr] = useState('');
  const [search, setSearch] = useState('');

  const fmtM = currentMonth
    ? new Date(currentMonth + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : '';
  const fmtPrev = prev
    ? new Date(prev + '-01').toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
    : '';

  const handleSave = useCallback((brandName, platform, val) => {
    setFollowerCount(brandName, currentMonth, platform, val);
  }, [currentMonth, setFollowerCount]);

  const filteredBrands = brands.filter(b => {
    const mgrMatch = !filterMgr || b.managerId === filterMgr;
    const searchMatch = !search || b.name.toLowerCase().includes(search.toLowerCase());
    return mgrMatch && searchMatch;
  });

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="page-title">Followers</h1>
        <p className="page-sub">{fmtM} · Click any number to edit inline</p>
      </div>

      <div className="filter-bar" style={{ marginBottom: 16 }}>
        <input
          className="search-box"
          placeholder="Search brand..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 200 }}
        />
        <select
          className="filter-select"
          value={filterMgr}
          onChange={e => setFilterMgr(e.target.value)}
        >
          <option value="">All managers</option>
          {managers.map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        {(filterMgr || search) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setFilterMgr(''); setSearch(''); }}>
            Clear
          </button>
        )}
      </div>

      <motion.div
        className="tbl-wrap"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .25 }}
      >
        <table style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '22%' }} />
            <col style={{ width: '13%' }} />
            {PLATFORMS.map(p => (
              <React.Fragment key={p}>
                <col style={{ width: '12%' }} />
                <col style={{ width: '6%' }} />
              </React.Fragment>
            ))}
          </colgroup>
          <thead>
            <tr>
              <th>Brand</th>
              <th>Manager</th>
              {PLATFORMS.map(p => (
                <React.Fragment key={p}>
                  <th style={{ textAlign: 'right' }}>{p}</th>
                  <th style={{ textAlign: 'right', color: 'var(--text3)', fontWeight: 400, fontSize: 10 }}>vs {fmtPrev}</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredBrands.length === 0 && (
              <tr>
                <td colSpan={2 + PLATFORMS.length * 2} style={{ textAlign: 'center', padding: 32, color: 'var(--text3)' }}>
                  No brands found
                </td>
              </tr>
            )}
            {filteredBrands.map((b, i) => {
              const mgr = managers.find(m => m.id === b.managerId);
              return (
                <motion.tr
                  key={b.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.02 }}
                >
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: mgr?.color || '#666', flexShrink: 0, display: 'inline-block' }} />
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{b.name}</span>
                    </div>
                  </td>
                  <td>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, color: '#000', fontWeight: 600, background: mgr?.color || '#888', display: 'inline-block' }}>
                      {mgr?.name}
                    </span>
                  </td>
                  {PLATFORMS.map(p => {
                    const curr = getFollowerCount(b.name, currentMonth, p);
                    const pv   = getFollowerCount(b.name, prev, p);
                    return (
                      <React.Fragment key={p}>
                        <td style={{ padding: '6px 8px' }}>
                          <EditableCell
                            value={curr}
                            onSave={val => handleSave(b.name, p, val)}
                          />
                        </td>
                        <td style={{ padding: '6px 4px' }}>
                          <DeltaBadge curr={curr} prev={pv} />
                        </td>
                      </React.Fragment>
                    );
                  })}
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </motion.div>

      <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 12 }}>
        Click any number to edit · Press Enter or click away to save · Switch months using the dropdown at the top
      </p>
    </div>
  );
}
